import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { 
  FiPackage, FiPlus, FiEdit, FiTrash2, FiSave, FiX, 
  FiPercent, FiClock, FiDollarSign, FiImage, FiTag,
  FiLayers, FiChevronDown, FiChevronUp, FiCopy, FiUpload,
  FiCheck, FiAlertCircle, FiEye, FiEyeOff, FiMove
} from 'react-icons/fi';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProductsFormulasPage() {
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [formulas, setFormulas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingFormula, setEditingFormula] = useState(null);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [showFormulaDialog, setShowFormulaDialog] = useState(false);
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const fileInputRef = useRef(null);

  // Form states
  const [productForm, setProductForm] = useState({
    name: '',
    short_description: '',
    description: '',
    category: 'photobooth',
    base_price: 0,
    is_featured: false,
    is_active: true,
    images: [],
    variations: [],
    options: []
  });

  const [formulaForm, setFormulaForm] = useState({
    name: '',
    description: '',
    short_description: '',
    image: '',
    included_product_ids: [],
    discount_type: 'percentage',
    discount_value: 15,
    duration_options: [],
    is_active: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Load products
      const productsRes = await fetch(`${API_URL}/api/public/products`);
      const productsData = await productsRes.json();
      setProducts(productsData.products || []);

      // Load formulas (include inactive ones for admin)
      const formulasRes = await fetch(`${API_URL}/api/products/formulas/global?active_only=false`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const formulasData = await formulasRes.json();
      setFormulas(formulasData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // Filter products by search
  const filteredProducts = products.filter(p => 
    p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredFormulas = formulas.filter(f =>
    f.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate statistics
  const stats = {
    totalProducts: products.length,
    activeProducts: products.filter(p => p.is_active !== false).length,
    totalFormulas: formulas.length,
    activeFormulas: formulas.filter(f => f.is_active !== false).length,
    totalVariations: products.reduce((acc, p) => acc + (p.variations?.length || 0), 0),
    totalOptions: products.reduce((acc, p) => acc + (p.options?.length || 0), 0)
  };

  // Image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/media/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        const imageUrl = data.url || data.file_url;
        setProductForm(prev => ({
          ...prev,
          images: [...prev.images, imageUrl]
        }));
        toast.success('Image uploadée');
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors de l\'upload');
    }
  };

  const removeImage = (index) => {
    setProductForm(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  // Product functions
  const handleSaveProduct = async () => {
    if (!productForm.name) {
      toast.error('Le nom du produit est requis');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const method = editingProduct ? 'PUT' : 'POST';
      const url = editingProduct 
        ? `${API_URL}/api/products/${editingProduct.id}`
        : `${API_URL}/api/products`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(productForm)
      });

      if (response.ok) {
        toast.success(editingProduct ? 'Produit mis à jour' : 'Produit créé');
        loadData();
        setShowProductDialog(false);
        resetProductForm();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save product');
      }
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/products/${productId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Produit supprimé');
        loadData();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const duplicateProduct = (product) => {
    setEditingProduct(null);
    setProductForm({
      name: `${product.name} (copie)`,
      short_description: product.short_description || '',
      description: product.description || '',
      category: product.category || 'photobooth',
      base_price: product.base_price || 0,
      is_featured: false,
      is_active: true,
      images: [...(product.images || [])],
      variations: (product.variations || []).map(v => ({
        ...v,
        id: `var-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      })),
      options: (product.options || []).map(o => ({
        ...o,
        id: `opt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      }))
    });
    setShowProductDialog(true);
  };

  const toggleProductActive = async (product) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/products/${product.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ...product, is_active: !product.is_active })
      });

      if (response.ok) {
        toast.success(product.is_active ? 'Produit désactivé' : 'Produit activé');
        loadData();
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const resetProductForm = () => {
    setProductForm({
      name: '',
      short_description: '',
      description: '',
      category: 'photobooth',
      base_price: 0,
      is_featured: false,
      is_active: true,
      images: [],
      cover_image: '', // Image for mega menu
      hero_video_id: '', // YouTube video ID for tarifs page background
      presentation_video_id: '', // YouTube video ID for presentation page background
      variations: [],
      options: []
    });
    setEditingProduct(null);
  };

  const editProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name || '',
      short_description: product.short_description || '',
      description: product.description || '',
      category: product.category || 'photobooth',
      base_price: product.base_price || 0,
      is_featured: product.is_featured || false,
      is_active: product.is_active !== false,
      images: product.images || [],
      cover_image: product.cover_image || '',
      hero_video_id: product.hero_video_id || '',
      presentation_video_id: product.presentation_video_id || '',
      variations: (product.variations || []).map(v => ({
        ...v,
        // Ensure styling properties have defaults
        showRibbon: v.showRibbon || false,
        ribbonText: v.ribbonText || 'BEST SELLER',
        ribbonColor: v.ribbonColor || '#DC2626',
        headerColor: v.headerColor || '#1a1a1a',
        backgroundType: v.backgroundType || 'solid',
        backgroundColor: v.backgroundColor || '#ffffff',
        gradientColors: v.gradientColors || ['#D4A574', '#C9A45C', '#B8956F'],
        textColorOverride: v.textColorOverride || ''
      })),
      options: product.options || []
    });
    setShowProductDialog(true);
  };

  // Variation functions
  const addVariation = () => {
    setProductForm(prev => ({
      ...prev,
      variations: [...prev.variations, {
        id: `var-${Date.now()}`,
        name: '',
        price: 0,
        duration_hours: 1,
        duration_label: '',
        description: '',
        is_active: true,
        is_popular: false,
        // Tariff details
        prints_count: null,
        prints_unlimited: false,
        templates_count: 1,
        music_count: 0,
        km_offerts: false,
        usb_included: false,
        features: [],
        // Card styling options
        showRibbon: false,
        ribbonText: 'BEST SELLER',
        ribbonColor: '#DC2626',
        headerColor: '#1a1a1a',
        backgroundType: 'solid',
        backgroundColor: '#ffffff',
        gradientColors: ['#D4A574', '#C9A45C', '#B8956F'],
        textColorOverride: ''
      }]
    }));
  };

  const updateVariation = (index, field, value) => {
    setProductForm(prev => ({
      ...prev,
      variations: prev.variations.map((v, i) => 
        i === index ? { ...v, [field]: value } : v
      )
    }));
  };

  const removeVariation = (index) => {
    setProductForm(prev => ({
      ...prev,
      variations: prev.variations.filter((_, i) => i !== index)
    }));
  };

  // Option functions
  const addOption = () => {
    setProductForm(prev => ({
      ...prev,
      options: [...prev.options, {
        id: `opt-${Date.now()}`,
        name: '',
        price: 0,
        description: '',
        is_active: true
      }]
    }));
  };

  const updateOption = (index, field, value) => {
    setProductForm(prev => ({
      ...prev,
      options: prev.options.map((o, i) => 
        i === index ? { ...o, [field]: value } : o
      )
    }));
  };

  const removeOption = (index) => {
    setProductForm(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  // Formula functions
  const handleSaveFormula = async () => {
    if (!formulaForm.name) {
      toast.error('Le nom de la formule est requis');
      return;
    }

    if (formulaForm.included_product_ids.length < 2) {
      toast.error('Sélectionnez au moins 2 produits');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      if (editingFormula) {
        const response = await fetch(`${API_URL}/api/products/formulas/global/${editingFormula.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(formulaForm)
        });

        if (response.ok) {
          toast.success('Formule mise à jour');
          loadData();
          setShowFormulaDialog(false);
          resetFormulaForm();
        }
      } else {
        const response = await fetch(`${API_URL}/api/products/formulas/global?site_id=default`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(formulaForm)
        });

        if (response.ok) {
          toast.success('Formule créée');
          loadData();
          setShowFormulaDialog(false);
          resetFormulaForm();
        }
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFormula = async (formulaId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette formule ?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/products/formulas/global/${formulaId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Formule supprimée');
        loadData();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const duplicateFormula = (formula) => {
    setEditingFormula(null);
    setFormulaForm({
      name: `${formula.name} (copie)`,
      description: formula.description || '',
      short_description: formula.short_description || '',
      image: formula.image || '',
      included_product_ids: [...(formula.included_product_ids || [])],
      discount_type: formula.discount_type || 'percentage',
      discount_value: formula.discount_value || 15,
      duration_options: (formula.duration_options || []).map(d => ({ ...d })),
      is_active: true
    });
    setShowFormulaDialog(true);
  };

  const toggleFormulaActive = async (formula) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/products/formulas/global/${formula.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ...formula, is_active: !formula.is_active })
      });

      if (response.ok) {
        toast.success(formula.is_active ? 'Formule désactivée' : 'Formule activée');
        loadData();
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const resetFormulaForm = () => {
    setFormulaForm({
      name: '',
      description: '',
      short_description: '',
      image: '',
      included_product_ids: [],
      discount_type: 'percentage',
      discount_value: 15,
      duration_options: [],
      is_active: true
    });
    setEditingFormula(null);
  };

  const editFormula = (formula) => {
    setEditingFormula(formula);
    setFormulaForm({
      name: formula.name || '',
      description: formula.description || '',
      short_description: formula.short_description || '',
      image: formula.image || '',
      included_product_ids: formula.included_product_ids || [],
      discount_type: formula.discount_type || 'percentage',
      discount_value: formula.discount_value || 15,
      duration_options: formula.duration_options || [],
      is_active: formula.is_active !== false
    });
    setShowFormulaDialog(true);
  };

  // Duration options for formula
  const addDurationOption = () => {
    setFormulaForm(prev => ({
      ...prev,
      duration_options: [...prev.duration_options, {
        hours: 3,
        price: 0,
        name: '3 heures'
      }]
    }));
  };

  const updateDurationOption = (index, field, value) => {
    setFormulaForm(prev => ({
      ...prev,
      duration_options: prev.duration_options.map((d, i) => 
        i === index ? { ...d, [field]: value } : d
      )
    }));
  };

  const removeDurationOption = (index) => {
    setFormulaForm(prev => ({
      ...prev,
      duration_options: prev.duration_options.filter((_, i) => i !== index)
    }));
  };

  const toggleProductInFormula = (productId) => {
    setFormulaForm(prev => ({
      ...prev,
      included_product_ids: prev.included_product_ids.includes(productId)
        ? prev.included_product_ids.filter(id => id !== productId)
        : [...prev.included_product_ids, productId]
    }));
  };

  // Calculate formula base price (sum of included products' min prices)
  const calculateFormulaBasePrice = (formula) => {
    let total = 0;
    (formula.included_product_ids || []).forEach(pid => {
      const product = products.find(p => p.id === pid);
      if (product?.variations?.length > 0) {
        const minPrice = Math.min(...product.variations.map(v => v.price || 0));
        total += minPrice;
      }
    });
    return total;
  };

  // Calculate discounted price
  const calculateDiscountedPrice = (formula) => {
    const basePrice = calculateFormulaBasePrice(formula);
    if (formula.discount_type === 'percentage') {
      return Math.round(basePrice * (1 - (formula.discount_value || 0) / 100));
    }
    return basePrice - (formula.discount_value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-200 border-t-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 bg-white rounded-xl p-6" data-testid="products-formulas-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-slate-200 pb-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Produits & Formules</h1>
          <p className="text-sm text-slate-600 mt-1">Gérez vos machines, tarifs et formules combinées</p>
        </div>
        <div className="flex items-center gap-3">
          <Input
            placeholder="Rechercher..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-48"
          />
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        <div className="bg-indigo-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-indigo-600">{stats.totalProducts}</p>
          <p className="text-xs text-indigo-700">Produits</p>
        </div>
        <div className="bg-emerald-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-emerald-600">{stats.activeProducts}</p>
          <p className="text-xs text-emerald-700">Actifs</p>
        </div>
        <div className="bg-violet-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-violet-600">{stats.totalFormulas}</p>
          <p className="text-xs text-violet-700">Formules</p>
        </div>
        <div className="bg-amber-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-amber-600">{stats.activeFormulas}</p>
          <p className="text-xs text-amber-700">Formules actives</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-blue-600">{stats.totalVariations}</p>
          <p className="text-xs text-blue-700">Variations</p>
        </div>
        <div className="bg-rose-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-rose-600">{stats.totalOptions}</p>
          <p className="text-xs text-rose-700">Options</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between">
          <TabsList className="grid w-auto grid-cols-2">
            <TabsTrigger value="products" className="flex items-center gap-2 px-6">
              <FiPackage className="w-4 h-4" />
              Produits ({filteredProducts.length})
            </TabsTrigger>
            <TabsTrigger value="formulas" className="flex items-center gap-2 px-6">
              <FiLayers className="w-4 h-4" />
              Formules ({filteredFormulas.length})
            </TabsTrigger>
          </TabsList>

          <Button 
            onClick={() => {
              if (activeTab === 'products') {
                resetProductForm();
                setShowProductDialog(true);
              } else {
                resetFormulaForm();
                setShowFormulaDialog(true);
              }
            }}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            <FiPlus className="w-4 h-4 mr-2" />
            {activeTab === 'products' ? 'Nouveau produit' : 'Nouvelle formule'}
          </Button>
        </div>

        {/* Products Tab */}
        <TabsContent value="products" className="space-y-4 mt-4">
          <div className="space-y-3">
            {filteredProducts.map(product => (
              <Card key={product.id} className={`overflow-hidden transition ${product.is_active === false ? 'opacity-60' : ''}`}>
                <div 
                  className="p-4 cursor-pointer hover:bg-slate-50 transition"
                  onClick={() => setExpandedProduct(expandedProduct === product.id ? null : product.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {product.images?.[0] ? (
                        <img 
                          src={product.images[0]} 
                          alt={product.name}
                          className="w-16 h-16 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center">
                          <FiImage className="w-6 h-6 text-slate-400" />
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-900">{product.name}</h3>
                          {product.is_featured && (
                            <Badge className="bg-amber-100 text-amber-700 text-xs">Vedette</Badge>
                          )}
                          {product.is_active === false && (
                            <Badge variant="outline" className="text-slate-500 text-xs">Inactif</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 line-clamp-1">{product.short_description}</p>
                        <div className="flex gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">{product.category}</Badge>
                          <Badge variant="outline" className="text-xs">{product.variations?.length || 0} formules</Badge>
                          {product.options?.length > 0 && (
                            <Badge variant="outline" className="text-xs">{product.options.length} options</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); toggleProductActive(product); }}
                        title={product.is_active !== false ? 'Désactiver' : 'Activer'}
                      >
                        {product.is_active !== false ? <FiEye className="w-4 h-4" /> : <FiEyeOff className="w-4 h-4" />}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); duplicateProduct(product); }}
                        title="Dupliquer"
                      >
                        <FiCopy className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); editProduct(product); }}
                        title="Modifier"
                      >
                        <FiEdit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        onClick={(e) => { e.stopPropagation(); handleDeleteProduct(product.id); }}
                        title="Supprimer"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </Button>
                      {expandedProduct === product.id ? (
                        <FiChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <FiChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>
                </div>

                <AnimatePresence>
                  {expandedProduct === product.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="border-t border-slate-100"
                    >
                      <div className="p-4 bg-slate-50">
                        {/* Variations */}
                        <div className="mb-4">
                          <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                            <FiClock className="w-4 h-4" />
                            Formules / Variations ({product.variations?.length || 0})
                          </h4>
                          {product.variations?.length > 0 ? (
                            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                              {product.variations.map(variation => (
                                <div 
                                  key={variation.id}
                                  className={`bg-white rounded-lg p-3 border ${variation.is_active === false ? 'border-slate-200 opacity-50' : 'border-slate-200'}`}
                                >
                                  <div className="flex justify-between items-start">
                                    <p className="font-medium text-slate-900">{variation.name}</p>
                                    {variation.is_active === false && (
                                      <Badge variant="outline" className="text-xs">Inactif</Badge>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2 mt-1 text-sm text-slate-600">
                                    <FiClock className="w-3 h-3" />
                                    {variation.duration_hours}h
                                  </div>
                                  <p className="text-lg font-bold text-indigo-600 mt-2">
                                    {variation.price}€
                                  </p>
                                  {variation.description && (
                                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">{variation.description}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-slate-500 italic">Aucune variation définie</p>
                          )}
                        </div>

                        {/* Options */}
                        {product.options?.length > 0 && (
                          <div>
                            <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                              <FiTag className="w-4 h-4" />
                              Options ({product.options.length})
                            </h4>
                            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                              {product.options.map(option => (
                                <div 
                                  key={option.id}
                                  className={`bg-white rounded-lg p-3 border ${option.is_active === false ? 'border-slate-200 opacity-50' : 'border-slate-200'}`}
                                >
                                  <p className="font-medium text-slate-900">{option.name}</p>
                                  <p className="text-lg font-bold text-emerald-600 mt-1">
                                    +{option.price}€
                                  </p>
                                  {option.description && (
                                    <p className="text-xs text-slate-500 mt-1">{option.description}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Card>
            ))}

            {filteredProducts.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <FiPackage className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>{searchQuery ? 'Aucun produit trouvé' : 'Aucun produit créé'}</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Formulas Tab */}
        <TabsContent value="formulas" className="space-y-4 mt-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredFormulas.map(formula => {
              const basePrice = calculateFormulaBasePrice(formula);
              const discountedPrice = calculateDiscountedPrice(formula);
              const savings = basePrice - discountedPrice;

              return (
                <Card key={formula.id} className={formula.is_active === false ? 'opacity-60' : ''}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <CardTitle className="text-lg">{formula.name}</CardTitle>
                          <Badge className="bg-emerald-100 text-emerald-700">
                            -{formula.discount_value}{formula.discount_type === 'percentage' ? '%' : '€'}
                          </Badge>
                          {formula.is_active === false && (
                            <Badge variant="outline" className="text-slate-500">Inactive</Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => toggleFormulaActive(formula)}
                          title={formula.is_active !== false ? 'Désactiver' : 'Activer'}
                        >
                          {formula.is_active !== false ? <FiEye className="w-4 h-4" /> : <FiEyeOff className="w-4 h-4" />}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => duplicateFormula(formula)}
                          title="Dupliquer"
                        >
                          <FiCopy className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => editFormula(formula)}
                          title="Modifier"
                        >
                          <FiEdit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleDeleteFormula(formula.id)}
                          title="Supprimer"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-600 mb-3 line-clamp-2">{formula.description}</p>
                    
                    {/* Included products */}
                    <div className="flex flex-wrap gap-1 mb-3">
                      {formula.included_product_ids?.map(pid => {
                        const product = products.find(p => p.id === pid);
                        return product ? (
                          <Badge key={pid} variant="outline" className="text-xs">
                            {product.name}
                          </Badge>
                        ) : null;
                      })}
                    </div>

                    {/* Price comparison */}
                    <div className="bg-slate-50 rounded-lg p-3 mb-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Prix séparé</span>
                        <span className="line-through text-slate-400">{basePrice}€</span>
                      </div>
                      <div className="flex justify-between text-sm font-medium">
                        <span className="text-emerald-600">Économie</span>
                        <span className="text-emerald-600">-{savings}€</span>
                      </div>
                    </div>

                    {/* Duration options */}
                    {formula.duration_options?.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-xs text-slate-500 font-medium mb-1">Durées disponibles :</p>
                        {formula.duration_options.map((dur, i) => (
                          <div key={i} className="flex justify-between text-sm">
                            <span className="text-slate-600">{dur.name || `${dur.hours}h`}</span>
                            <span className="font-semibold text-indigo-600">{dur.price}€</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}

            {filteredFormulas.length === 0 && (
              <div className="col-span-full text-center py-12 text-slate-500">
                <FiLayers className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>{searchQuery ? 'Aucune formule trouvée' : 'Aucune formule créée'}</p>
                <p className="text-sm mt-1">Les formules permettent de combiner plusieurs machines avec une réduction</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Product Dialog */}
      <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FiPackage className="w-5 h-5" />
              {editingProduct ? 'Modifier le produit' : 'Nouveau produit'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label>Nom du produit *</Label>
                <Input
                  value={productForm.name}
                  onChange={(e) => setProductForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Photobooth Premium"
                />
              </div>
              <div>
                <Label>Catégorie</Label>
                <Select 
                  value={productForm.category}
                  onValueChange={(v) => setProductForm(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="photobooth">Photobooth</SelectItem>
                    <SelectItem value="videobooth">VideoBooth</SelectItem>
                    <SelectItem value="livre-dor">Livre d'Or</SelectItem>
                    <SelectItem value="pack">Pack</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Prix de base (€)</Label>
                <Input
                  type="number"
                  value={productForm.base_price}
                  onChange={(e) => setProductForm(prev => ({ ...prev, base_price: parseFloat(e.target.value) || 0 }))}
                />
              </div>
            </div>

            <div>
              <Label>Description courte</Label>
              <Input
                value={productForm.short_description}
                onChange={(e) => setProductForm(prev => ({ ...prev, short_description: e.target.value }))}
                placeholder="Description affichée sur les cartes"
              />
            </div>

            <div>
              <Label>Description complète</Label>
              <Textarea
                value={productForm.description}
                onChange={(e) => setProductForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Description détaillée du produit"
                rows={3}
              />
            </div>

            {/* Images */}
            <div>
              <Label className="mb-2 block">Images</Label>
              <div className="flex flex-wrap gap-2">
                {productForm.images.map((img, index) => (
                  <div key={index} className="relative group">
                    <img src={img} alt="" className="w-20 h-20 rounded-lg object-cover" />
                    <button
                      onClick={() => removeImage(index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                    >
                      <FiX className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center hover:border-indigo-400 transition"
                >
                  <FiUpload className="w-6 h-6 text-slate-400" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>
            </div>

            {/* Switches */}
            <div className="flex gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={productForm.is_active}
                  onCheckedChange={(v) => setProductForm(prev => ({ ...prev, is_active: v }))}
                />
                <Label>Actif</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={productForm.is_featured}
                  onCheckedChange={(v) => setProductForm(prev => ({ ...prev, is_featured: v }))}
                />
                <Label>Produit vedette</Label>
              </div>
            </div>

            {/* Variations */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <FiClock className="w-4 h-4" />
                  Formules / Variations
                </Label>
                <Button variant="outline" size="sm" onClick={addVariation}>
                  <FiPlus className="w-4 h-4 mr-1" />
                  Ajouter
                </Button>
              </div>
              <div className="space-y-4">
                {productForm.variations.map((variation, index) => (
                  <div key={variation.id} className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                    {/* Row 1: Basic Info */}
                    <div className="grid gap-3 sm:grid-cols-4 mb-3">
                      <Input
                        placeholder="Nom (ex: La Premium)"
                        value={variation.name}
                        onChange={(e) => updateVariation(index, 'name', e.target.value)}
                      />
                      <Input
                        type="number"
                        placeholder="Prix €"
                        value={variation.price}
                        onChange={(e) => updateVariation(index, 'price', parseFloat(e.target.value) || 0)}
                      />
                      <Input
                        type="number"
                        placeholder="Durée (h)"
                        value={variation.duration_hours || ''}
                        onChange={(e) => updateVariation(index, 'duration_hours', parseFloat(e.target.value) || null)}
                      />
                      <Input
                        placeholder="Libellé durée (ex: 3h, 24h max)"
                        value={variation.duration_label || ''}
                        onChange={(e) => updateVariation(index, 'duration_label', e.target.value)}
                      />
                    </div>
                    
                    {/* Row 2: Tariff Details */}
                    <div className="bg-white p-3 rounded border border-slate-100 mb-3">
                      <p className="text-xs text-slate-500 font-medium mb-2">Détails de l'offre (affichés sur la carte tarif)</p>
                      <div className="grid gap-3 sm:grid-cols-4">
                        <div>
                          <label className="text-xs text-slate-500 block mb-1">Photos imprimées</label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              placeholder="Nb"
                              value={variation.prints_count || ''}
                              onChange={(e) => updateVariation(index, 'prints_count', e.target.value ? parseInt(e.target.value) : null)}
                              disabled={variation.prints_unlimited}
                              className="w-20"
                            />
                            <label className="flex items-center gap-1 text-xs">
                              <input
                                type="checkbox"
                                checked={variation.prints_unlimited || false}
                                onChange={(e) => updateVariation(index, 'prints_unlimited', e.target.checked)}
                                className="rounded"
                              />
                              Illimité
                            </label>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 block mb-1">Modèles inclus</label>
                          <Input
                            type="number"
                            min="1"
                            value={variation.templates_count || 1}
                            onChange={(e) => updateVariation(index, 'templates_count', parseInt(e.target.value) || 1)}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 block mb-1">Musiques (360)</label>
                          <Input
                            type="number"
                            min="0"
                            value={variation.music_count || 0}
                            onChange={(e) => updateVariation(index, 'music_count', parseInt(e.target.value) || 0)}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 block mb-1">Bonus</label>
                          <div className="flex items-center gap-3">
                            <label className="flex items-center gap-1 text-xs">
                              <input
                                type="checkbox"
                                checked={variation.km_offerts || false}
                                onChange={(e) => updateVariation(index, 'km_offerts', e.target.checked)}
                                className="rounded"
                              />
                              Km offerts
                            </label>
                            <label className="flex items-center gap-1 text-xs">
                              <input
                                type="checkbox"
                                checked={variation.usb_included || false}
                                onChange={(e) => updateVariation(index, 'usb_included', e.target.checked)}
                                className="rounded"
                              />
                              Clé USB
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Row 3: Features List */}
                    <div className="mb-3">
                      <label className="text-xs text-slate-500 block mb-1">Liste des services inclus (un par ligne)</label>
                      <textarea
                        className="w-full text-sm border border-slate-200 rounded-md p-2 min-h-[80px]"
                        placeholder="200 photos imprimées haute qualité&#10;Photos numériques en illimité&#10;1 modèle personnalisé inclus&#10;Galerie photo sécurisée&#10;Récupération instantanée via QR Code"
                        value={(variation.features || []).join('\n')}
                        onChange={(e) => updateVariation(index, 'features', e.target.value.split('\n').filter(f => f.trim()))}
                      />
                    </div>
                    
                    {/* Row 4: Card Styling Options */}
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-3 rounded border border-amber-200 mb-3">
                      <p className="text-xs text-amber-700 font-semibold mb-3 flex items-center gap-1">
                        🎨 Style de la carte tarifaire
                      </p>
                      
                      {/* Ribbon settings */}
                      <div className="grid gap-3 sm:grid-cols-4 mb-3">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={variation.showRibbon || false}
                            onCheckedChange={(v) => updateVariation(index, 'showRibbon', v)}
                          />
                          <span className="text-xs text-slate-600">Afficher ruban</span>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 block mb-1">Texte du ruban</label>
                          <Input
                            placeholder="BEST SELLER"
                            value={variation.ribbonText || ''}
                            onChange={(e) => updateVariation(index, 'ribbonText', e.target.value)}
                            disabled={!variation.showRibbon}
                            className="text-xs h-8"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 block mb-1">Couleur ruban</label>
                          <div className="flex gap-1">
                            <input
                              type="color"
                              value={variation.ribbonColor || '#DC2626'}
                              onChange={(e) => updateVariation(index, 'ribbonColor', e.target.value)}
                              className="w-8 h-8 rounded cursor-pointer"
                              disabled={!variation.showRibbon}
                            />
                            <Input
                              value={variation.ribbonColor || '#DC2626'}
                              onChange={(e) => updateVariation(index, 'ribbonColor', e.target.value)}
                              className="flex-1 text-xs h-8"
                              disabled={!variation.showRibbon}
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 block mb-1">Couleur en-tête</label>
                          <div className="flex gap-1">
                            <input
                              type="color"
                              value={variation.headerColor || '#1a1a1a'}
                              onChange={(e) => updateVariation(index, 'headerColor', e.target.value)}
                              className="w-8 h-8 rounded cursor-pointer"
                            />
                            <Input
                              value={variation.headerColor || '#1a1a1a'}
                              onChange={(e) => updateVariation(index, 'headerColor', e.target.value)}
                              className="flex-1 text-xs h-8"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Background settings */}
                      <div className="grid gap-3 sm:grid-cols-4">
                        <div>
                          <label className="text-xs text-slate-500 block mb-1">Type de fond</label>
                          <Select 
                            value={variation.backgroundType || 'solid'}
                            onValueChange={(v) => updateVariation(index, 'backgroundType', v)}
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="solid">Couleur unie</SelectItem>
                              <SelectItem value="gradient">Dégradé (doré/rouge)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {variation.backgroundType === 'solid' ? (
                          <div>
                            <label className="text-xs text-slate-500 block mb-1">Couleur de fond</label>
                            <div className="flex gap-1">
                              <input
                                type="color"
                                value={variation.backgroundColor || '#ffffff'}
                                onChange={(e) => updateVariation(index, 'backgroundColor', e.target.value)}
                                className="w-8 h-8 rounded cursor-pointer"
                              />
                              <Input
                                value={variation.backgroundColor || '#ffffff'}
                                onChange={(e) => updateVariation(index, 'backgroundColor', e.target.value)}
                                className="flex-1 text-xs h-8"
                              />
                            </div>
                          </div>
                        ) : (
                          <>
                            <div>
                              <label className="text-xs text-slate-500 block mb-1">Dégradé (haut)</label>
                              <div className="flex gap-1">
                                <input
                                  type="color"
                                  value={(variation.gradientColors || ['#D4A574'])[0]}
                                  onChange={(e) => {
                                    const colors = variation.gradientColors || ['#D4A574', '#C9A45C', '#B8956F'];
                                    updateVariation(index, 'gradientColors', [e.target.value, colors[1], colors[2]]);
                                  }}
                                  className="w-8 h-8 rounded cursor-pointer"
                                />
                                <Input
                                  value={(variation.gradientColors || ['#D4A574'])[0]}
                                  onChange={(e) => {
                                    const colors = variation.gradientColors || ['#D4A574', '#C9A45C', '#B8956F'];
                                    updateVariation(index, 'gradientColors', [e.target.value, colors[1], colors[2]]);
                                  }}
                                  className="flex-1 text-xs h-8"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="text-xs text-slate-500 block mb-1">Dégradé (milieu)</label>
                              <div className="flex gap-1">
                                <input
                                  type="color"
                                  value={(variation.gradientColors || ['#D4A574', '#C9A45C'])[1]}
                                  onChange={(e) => {
                                    const colors = variation.gradientColors || ['#D4A574', '#C9A45C', '#B8956F'];
                                    updateVariation(index, 'gradientColors', [colors[0], e.target.value, colors[2]]);
                                  }}
                                  className="w-8 h-8 rounded cursor-pointer"
                                />
                                <Input
                                  value={(variation.gradientColors || ['#D4A574', '#C9A45C'])[1]}
                                  onChange={(e) => {
                                    const colors = variation.gradientColors || ['#D4A574', '#C9A45C', '#B8956F'];
                                    updateVariation(index, 'gradientColors', [colors[0], e.target.value, colors[2]]);
                                  }}
                                  className="flex-1 text-xs h-8"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="text-xs text-slate-500 block mb-1">Dégradé (bas)</label>
                              <div className="flex gap-1">
                                <input
                                  type="color"
                                  value={(variation.gradientColors || ['#D4A574', '#C9A45C', '#B8956F'])[2]}
                                  onChange={(e) => {
                                    const colors = variation.gradientColors || ['#D4A574', '#C9A45C', '#B8956F'];
                                    updateVariation(index, 'gradientColors', [colors[0], colors[1], e.target.value]);
                                  }}
                                  className="w-8 h-8 rounded cursor-pointer"
                                />
                                <Input
                                  value={(variation.gradientColors || ['#D4A574', '#C9A45C', '#B8956F'])[2]}
                                  onChange={(e) => {
                                    const colors = variation.gradientColors || ['#D4A574', '#C9A45C', '#B8956F'];
                                    updateVariation(index, 'gradientColors', [colors[0], colors[1], e.target.value]);
                                  }}
                                  className="flex-1 text-xs h-8"
                                />
                              </div>
                            </div>
                          </>
                        )}
                      </div>

                      {/* Preview mini card */}
                      {(variation.showRibbon || variation.backgroundType === 'gradient') && (
                        <div className="mt-3 flex items-center gap-3">
                          <span className="text-xs text-slate-500">Aperçu:</span>
                          <div 
                            className="w-20 h-12 rounded-lg relative overflow-hidden shadow-sm border"
                            style={{
                              background: variation.backgroundType === 'gradient' 
                                ? `linear-gradient(180deg, ${(variation.gradientColors || ['#D4A574', '#C9A45C', '#B8956F'])[0]} 0%, ${(variation.gradientColors || ['#D4A574', '#C9A45C', '#B8956F'])[1]} 50%, ${(variation.gradientColors || ['#D4A574', '#C9A45C', '#B8956F'])[2]} 100%)`
                                : variation.backgroundColor || '#ffffff'
                            }}
                          >
                            <div 
                              className="h-3 w-full"
                              style={{ backgroundColor: variation.headerColor || '#1a1a1a' }}
                            />
                            {variation.showRibbon && (
                              <div 
                                className="absolute top-0 right-0 text-[4px] text-white px-1 py-0.5"
                                style={{ backgroundColor: variation.ribbonColor || '#DC2626', transform: 'rotate(45deg) translate(15%, -30%)' }}
                              >
                                {(variation.ribbonText || 'BEST').slice(0, 4)}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Row 5: Controls */}
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={variation.is_active !== false}
                            onCheckedChange={(v) => updateVariation(index, 'is_active', v)}
                          />
                          <span className="text-xs text-slate-500">Active</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={variation.is_popular || false}
                            onCheckedChange={(v) => updateVariation(index, 'is_popular', v)}
                          />
                          <span className="text-xs text-slate-500">⭐ Populaire</span>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 h-8"
                        onClick={() => removeVariation(index)}
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {productForm.variations.length === 0 && (
                  <p className="text-sm text-slate-500 italic text-center py-4">
                    Aucune variation. Cliquez sur "Ajouter" pour créer des formules avec différentes durées et prix.
                  </p>
                )}
              </div>
            </div>

            {/* Options */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <FiTag className="w-4 h-4" />
                  Options supplémentaires
                </Label>
                <Button variant="outline" size="sm" onClick={addOption}>
                  <FiPlus className="w-4 h-4 mr-1" />
                  Ajouter
                </Button>
              </div>
              <div className="space-y-3">
                {productForm.options.map((option, index) => (
                  <div key={option.id} className="bg-slate-50 p-3 rounded-lg">
                    <div className="grid gap-2 sm:grid-cols-4">
                      <Input
                        placeholder="Nom (ex: Montage vidéo)"
                        value={option.name}
                        onChange={(e) => updateOption(index, 'name', e.target.value)}
                      />
                      <Input
                        type="number"
                        placeholder="Prix"
                        value={option.price}
                        onChange={(e) => updateOption(index, 'price', parseFloat(e.target.value) || 0)}
                      />
                      <Input
                        placeholder="Description"
                        value={option.description || ''}
                        onChange={(e) => updateOption(index, 'description', e.target.value)}
                        className="sm:col-span-2"
                      />
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={option.is_active !== false}
                          onCheckedChange={(v) => updateOption(index, 'is_active', v)}
                        />
                        <span className="text-xs text-slate-500">Active</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 h-8"
                        onClick={() => removeOption(index)}
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {productForm.options.length === 0 && (
                  <p className="text-sm text-slate-500 italic text-center py-4">
                    Aucune option. Les options sont des services additionnels (ex: impressions supplémentaires, montage vidéo...)
                  </p>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowProductDialog(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleSaveProduct} 
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <FiSave className="w-4 h-4 mr-2" />
                    Enregistrer
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Formula Dialog */}
      <Dialog open={showFormulaDialog} onOpenChange={setShowFormulaDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FiLayers className="w-5 h-5" />
              {editingFormula ? 'Modifier la formule' : 'Nouvelle formule'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <div>
              <Label>Nom de la formule *</Label>
              <Input
                value={formulaForm.name}
                onChange={(e) => setFormulaForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ex: Formule Duo Classic"
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={formulaForm.description}
                onChange={(e) => setFormulaForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Description de la formule"
                rows={2}
              />
            </div>

            {/* Select products */}
            <div>
              <Label>Produits inclus * (min. 2)</Label>
              <p className="text-xs text-slate-500 mb-2">Sélectionnez les machines incluses dans cette formule</p>
              <div className="grid gap-2 sm:grid-cols-2 max-h-48 overflow-y-auto p-1">
                {products.filter(p => p.category !== 'gift-card' && p.name !== 'Carte Cadeau').map(product => (
                  <div
                    key={product.id}
                    onClick={() => toggleProductInFormula(product.id)}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition ${
                      formulaForm.included_product_ids.includes(product.id)
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {product.images?.[0] ? (
                        <img src={product.images[0]} alt="" className="w-10 h-10 rounded object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center">
                          <FiPackage className="w-5 h-5 text-slate-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-sm block truncate">{product.name}</span>
                        <span className="text-xs text-slate-500">
                          À partir de {Math.min(...(product.variations?.map(v => v.price) || [0]))}€
                        </span>
                      </div>
                      {formulaForm.included_product_ids.includes(product.id) && (
                        <FiCheck className="w-5 h-5 text-indigo-600" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {formulaForm.included_product_ids.length < 2 && (
                <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                  <FiAlertCircle className="w-3 h-3" />
                  Sélectionnez au moins 2 produits
                </p>
              )}
            </div>

            {/* Discount */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Type de réduction</Label>
                <Select 
                  value={formulaForm.discount_type}
                  onValueChange={(v) => setFormulaForm(prev => ({ ...prev, discount_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Pourcentage (%)</SelectItem>
                    <SelectItem value="fixed">Montant fixe (€)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Valeur de la réduction</Label>
                <div className="relative">
                  <Input
                    type="number"
                    value={formulaForm.discount_value}
                    onChange={(e) => setFormulaForm(prev => ({ ...prev, discount_value: parseFloat(e.target.value) || 0 }))}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                    {formulaForm.discount_type === 'percentage' ? '%' : '€'}
                  </span>
                </div>
              </div>
            </div>

            {/* Duration options */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Options de durée et prix</Label>
                <Button variant="outline" size="sm" onClick={addDurationOption}>
                  <FiPlus className="w-4 h-4 mr-1" />
                  Ajouter
                </Button>
              </div>
              <div className="space-y-2">
                {formulaForm.duration_options.map((dur, index) => (
                  <div key={index} className="flex gap-2 items-center bg-slate-50 p-2 rounded-lg">
                    <Input
                      placeholder="Nom (ex: 3 heures)"
                      value={dur.name || ''}
                      onChange={(e) => updateDurationOption(index, 'name', e.target.value)}
                      className="flex-1"
                    />
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        placeholder="h"
                        value={dur.hours}
                        onChange={(e) => updateDurationOption(index, 'hours', parseInt(e.target.value) || 1)}
                        className="w-16"
                      />
                      <span className="text-slate-500 text-sm">h</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        placeholder="Prix"
                        value={dur.price}
                        onChange={(e) => updateDurationOption(index, 'price', parseFloat(e.target.value) || 0)}
                        className="w-24"
                      />
                      <span className="text-slate-500 text-sm">€</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 h-8 w-8 p-0"
                      onClick={() => removeDurationOption(index)}
                    >
                      <FiX className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                {formulaForm.duration_options.length === 0 && (
                  <p className="text-sm text-slate-500 italic text-center py-3">
                    Ajoutez des options de durée avec leurs prix
                  </p>
                )}
              </div>
            </div>

            {/* Active */}
            <div className="flex items-center gap-2">
              <Switch
                checked={formulaForm.is_active}
                onCheckedChange={(v) => setFormulaForm(prev => ({ ...prev, is_active: v }))}
              />
              <Label>Formule active</Label>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowFormulaDialog(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleSaveFormula} 
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                disabled={saving || formulaForm.included_product_ids.length < 2}
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <FiSave className="w-4 h-4 mr-2" />
                    Enregistrer
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
